import { createFileRoute, Link } from "@tanstack/react-router";
import {
  ExternalLink,
  Mail,
  MapPin,
  Phone,
  ShieldCheck,
  Star,
  Building2,
} from "lucide-react";
import { SiteLayout } from "@/components/SiteLayout";
import { PageHero } from "@/components/PageHero";
import { FinalCTA } from "@/components/home/HomeSections";
import { SCHEMA_ORG_ID, SITE_URL } from "@/lib/entitySchema";

const CANONICAL = `${SITE_URL}/featured-in`;

const NAP = {
  name: "Colorado Web Impressions",
  street: "3210 E Woodmen Rd STE 110",
  city: "Colorado Springs",
  region: "CO",
  postal: "80920",
  phoneDisplay: "(719) 419-3935",
  phoneTel: "+17194193935",
  email: "info@coloradowebimpressions.com",
  founded: "2006",
} as const;

type Profile = {
  platform: string;
  url: string;
  handle: string;
  note: string;
  rating?: string;
  status?: "live" | "pending";
};

type ProfileGroup = {
  heading: string;
  description: string;
  icon: typeof ShieldCheck;
  profiles: Profile[];
};

const GROUPS: ProfileGroup[] = [
  {
    heading: "Review & rating platforms",
    description: "Independently verified reviews and accreditation profiles.",
    icon: Star,
    profiles: [
      {
        platform: "Better Business Bureau (BBB)",
        url: "https://www.bbb.org/us/co/colorado-springs/profile/web-design/colorado-web-impressions-0785-87368646",
        handle: "Colorado Web Impressions — Web Design",
        note: "Accredited business profile with verified Woodmen Rd address.",
      },
      {
        platform: "DesignRush",
        url: "https://www.designrush.com/agency/profile/colorado-web-impressions",
        handle: "Colorado Web Impressions",
        note: "Vetted agency directory.",
        rating: "5.0 / 5",
      },
      {
        platform: "Yelp",
        url: "https://www.yelp.com/biz/colorado-web-impressions-colorado-springs",
        handle: "Colorado Web Impressions — Colorado Springs",
        note: "Local business listing with NAP and reviews.",
      },
      {
        platform: "Google Business Profile",
        url: "https://www.google.com/search?q=Colorado+Web+Impressions+Colorado+Springs",
        handle: "Colorado Web Impressions",
        note: "Primary local search and Maps presence.",
      },
    ],
  },
  {
    heading: "Social & content platforms",
    description: "Owned channels where we publish and engage daily.",
    icon: Building2,
    profiles: [
      {
        platform: "Facebook",
        url: "https://www.facebook.com/coloradowebimpressions/",
        handle: "@coloradowebimpressions",
        note: "Company page — updates, events, community.",
      },
      {
        platform: "Instagram",
        url: "https://www.instagram.com/coloradowebimpressions/",
        handle: "@coloradowebimpressions",
        note: "Behind-the-scenes and client wins.",
      },
      {
        platform: "LinkedIn",
        url: "https://www.linkedin.com/company/colorado-web-impressions/",
        handle: "Colorado Web Impressions",
        note: "Company profile with team and case studies.",
      },
      {
        platform: "YouTube",
        url: "https://www.youtube.com/channel/UCobHjvCeHGsOsHFDp9cAOlQ",
        handle: "Colorado Web Impressions",
        note: "Strategy walkthroughs and how-to videos.",
      },
      {
        platform: "X (Twitter)",
        url: "https://x.com/cwebimpressions",
        handle: "@cwebimpressions",
        note: "Quick takes on local SEO and AI search.",
      },
      {
        platform: "Spotify — Honest Answers Podcast",
        url: "https://open.spotify.com/show/1QEaGYsfCspVL17n2CCje4",
        handle: "Honest Answers",
        note: "Weekly podcast on small-business marketing.",
      },
    ],
  },
  {
    heading: "Profiles in progress",
    description:
      "We're actively claiming and verifying these directories. Listings will appear here once NAP is verified.",
    icon: ShieldCheck,
    profiles: [
      {
        platform: "Clutch",
        url: "https://clutch.co",
        handle: "Profile pending",
        note: "Verification in progress.",
        status: "pending",
      },
      {
        platform: "UpCity",
        url: "https://upcity.com",
        handle: "Profile pending",
        note: "Verification in progress.",
        status: "pending",
      },
      {
        platform: "The Manifest",
        url: "https://themanifest.com",
        handle: "Profile pending",
        note: "Verification in progress.",
        status: "pending",
      },
      {
        platform: "GoodFirms",
        url: "https://www.goodfirms.co",
        handle: "Profile pending",
        note: "Verification in progress.",
        status: "pending",
      },
    ],
  },
];

const ALL_LIVE_URLS = GROUPS.flatMap((g) =>
  g.profiles.filter((p) => p.status !== "pending").map((p) => p.url),
);

const FEATURED_LD = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "CollectionPage",
      "@id": `${CANONICAL}#page`,
      url: CANONICAL,
      name: "Verified Profiles & Citations — Colorado Web Impressions",
      description:
        "Directory profiles, social channels, and citation listings for Colorado Web Impressions, with consistent Name, Address, and Phone (NAP) details.",
      isPartOf: { "@id": `${SITE_URL}/#website` },
      mainEntity: { "@id": SCHEMA_ORG_ID },
    },
    {
      "@type": "Organization",
      "@id": SCHEMA_ORG_ID,
      name: NAP.name,
      url: SITE_URL,
      telephone: "+1-719-419-3935",
      email: NAP.email,
      foundingDate: NAP.founded,
      address: {
        "@type": "PostalAddress",
        streetAddress: NAP.street,
        addressLocality: NAP.city,
        addressRegion: NAP.region,
        postalCode: NAP.postal,
        addressCountry: "US",
      },
      sameAs: ALL_LIVE_URLS,
    },
    {
      "@type": "BreadcrumbList",
      "@id": `${CANONICAL}#breadcrumb`,
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: SITE_URL },
        { "@type": "ListItem", position: 2, name: "Featured In", item: CANONICAL },
      ],
    },
  ],
};

export const Route = createFileRoute("/featured-in")({
  head: () => ({
    meta: [
        { name: "description", content: "Verified press mentions, citations, and profiles featuring Colorado Web Impressions and owner Chris Heidlebaugh across trusted industry publications." },
      {
        title: "Featured In — Press & Citations | CWI",
      },
      {
        name: "description",
        content:
          "Verified directory profiles, social channels, and citations for Colorado Web Impressions. Consistent NAP across BBB, DesignRush, Yelp, LinkedIn, and more.",
      },
      {
        property: "og:title",
        content: "Featured In — Colorado Web Impressions",
      },
      {
        property: "og:description",
        content:
          "Where to verify Colorado Web Impressions across the web — directories, social, and review platforms with consistent NAP.",
      },
      { property: "og:url", content: CANONICAL },
    ],
    links: [{ rel: "canonical", href: CANONICAL }],
    scripts: [
      { type: "application/ld+json", children: JSON.stringify(FEATURED_LD) },
    ],
  }),
  component: FeaturedInPage,
});

function NapBlock({ compact = false }: { compact?: boolean }) {
  return (
    <dl
      className={
        compact
          ? "mt-3 space-y-1 border-t border-border pt-3 text-xs text-muted-foreground"
          : "mt-4 grid gap-3 text-sm text-foreground sm:grid-cols-2"
      }
    >
      <div>
        <dt className="font-bold uppercase tracking-wider text-brand-gold-deep text-[10px]">
          Name
        </dt>
        <dd className={compact ? "" : "font-semibold"}>{NAP.name}</dd>
      </div>
      <div>
        <dt className="font-bold uppercase tracking-wider text-brand-gold-deep text-[10px]">
          Address
        </dt>
        <dd>
          {NAP.street}, {NAP.city}, {NAP.region} {NAP.postal}
        </dd>
      </div>
      <div>
        <dt className="font-bold uppercase tracking-wider text-brand-gold-deep text-[10px]">
          Phone
        </dt>
        <dd>{NAP.phoneDisplay}</dd>
      </div>
      {!compact && (
        <div>
          <dt className="font-bold uppercase tracking-wider text-brand-gold-deep text-[10px]">
            Email
          </dt>
          <dd>{NAP.email}</dd>
        </div>
      )}
    </dl>
  );
}

function FeaturedInPage() {
  return (
    <SiteLayout>
      <PageHero
        eyebrow="Featured in & verified"
        title={
          <>
            Verified profiles &{" "}
            <span className="text-brand-gold">consistent NAP</span> across the web.
          </>
        }
        description="Directory listings, review platforms, and social profiles where Colorado Web Impressions is verified. Same Name, Address, and Phone everywhere — the way Google likes it."
      />

      <section className="bg-background py-16 lg:py-20">
        <div className="mx-auto max-w-5xl px-5 lg:px-8">
          {/* Canonical NAP card */}
          <div className="rounded-2xl border-2 border-brand-gold/40 bg-card p-6 shadow-card sm:p-8">
            <div className="flex items-center gap-2">
              <ShieldCheck className="h-5 w-5 text-brand-gold-deep" />
              <h2 className="text-xl font-extrabold text-foreground">
                Our official business details
              </h2>
            </div>
            <p className="mt-2 text-sm text-muted-foreground">
              This is the canonical NAP we use on every directory below. If you
              spot a listing with a different address or phone, that listing is
              outdated — please use these details.
            </p>
            <NapBlock />
            <div className="mt-5 flex flex-wrap gap-3 text-sm">
              <a
                href={`tel:${NAP.phoneTel}`}
                className="inline-flex items-center gap-1.5 rounded-full bg-brand-gold/10 px-3 py-1.5 font-bold text-brand-gold-deep hover:bg-brand-gold/20"
               aria-label="Call (719) 419-3935">
                <Phone className="h-3.5 w-3.5" />
                {NAP.phoneDisplay}
              </a>
              <a
                href={`mailto:${NAP.email}`}
                className="inline-flex items-center gap-1.5 rounded-full bg-brand-gold/10 px-3 py-1.5 font-bold text-brand-gold-deep hover:bg-brand-gold/20"
               aria-label="Email Colorado Web Impressions">
                <Mail className="h-3.5 w-3.5" />
                {NAP.email}
              </a>
              <span className="inline-flex items-center gap-1.5 rounded-full bg-muted px-3 py-1.5 font-semibold text-muted-foreground">
                <MapPin className="h-3.5 w-3.5" />
                Founded {NAP.founded}
              </span>
            </div>
          </div>

          {/* Profile groups */}
          <div className="mt-14 space-y-14">
            {GROUPS.map((group) => {
              const Icon = group.icon;
              return (
                <div key={group.heading}>
                  <div className="flex items-center gap-2">
                    <Icon className="h-5 w-5 text-brand-gold-deep" />
                    <h2 className="text-2xl font-extrabold text-foreground">
                      {group.heading}
                    </h2>
                  </div>
                  <p className="mt-1.5 text-sm text-muted-foreground">
                    {group.description}
                  </p>

                  <ul className="mt-6 grid gap-4 sm:grid-cols-2">
                    {group.profiles.map((p) => {
                      const isPending = p.status === "pending";
                      const Wrap = isPending ? "div" : "a";
                      const wrapProps = isPending
                        ? {}
                        : {
                            href: p.url,
                            target: "_blank",
                            rel: "noopener noreferrer me",
                          };
                      return (
                        <li key={p.platform}>
                          <Wrap
                            {...wrapProps}
                            className={
                              "group flex h-full flex-col rounded-2xl border p-5 shadow-card transition-smooth " +
                              (isPending
                                ? "border-dashed border-border bg-muted/30 opacity-80"
                                : "border-border bg-card hover:border-brand-gold/50 hover:shadow-card-hover")
                            }
                          >
                            <div className="flex items-start justify-between gap-3">
                              <div>
                                <h3 className="text-base font-extrabold leading-snug text-foreground group-hover:text-brand-gold-deep">
                                  {p.platform}
                                </h3>
                                <p className="mt-0.5 text-xs font-semibold text-brand-gold-deep">
                                  {p.handle}
                                </p>
                              </div>
                              {p.rating && (
                                <span className="inline-flex items-center gap-1 rounded-full bg-brand-gold/10 px-2 py-0.5 text-[11px] font-bold text-brand-gold-deep">
                                  <Star className="h-3 w-3 fill-current" />
                                  {p.rating}
                                </span>
                              )}
                              {isPending && (
                                <span className="rounded-full bg-muted px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                                  Pending
                                </span>
                              )}
                            </div>
                            <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                              {p.note}
                            </p>
                            {!isPending && (
                              <div className="mt-3 inline-flex items-center gap-1.5 text-xs font-bold text-brand-gold-deep">
                                Visit profile
                                <ExternalLink className="h-3 w-3" />
                              </div>
                            )}
                            <NapBlock compact />
                          </Wrap>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              );
            })}
          </div>

          {/* Cross-links */}
          <div className="mt-14 rounded-2xl border border-border bg-muted/40 p-6 sm:p-8">
            <h2 className="text-xl font-extrabold text-foreground">
              Looking for editorial mentions?
            </h2>
            <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
              This page covers directories and citations. For podcast
              appearances, speaking engagements, and press quotes, visit our{" "}
              <Link
                to="/press"
                className="font-bold text-brand-gold-deep hover:underline"
              >
                Press &amp; Media page
              </Link>
              . You can also{" "}
              <Link
                to="/meet-chris"
                className="font-bold text-brand-gold-deep hover:underline"
              >
                meet the founder
              </Link>{" "}
              or{" "}
              <Link
                to="/reviews"
                className="font-bold text-brand-gold-deep hover:underline"
              >
                read client reviews
              </Link>
              .
            </p>
          </div>
        </div>
      </section>

      <FinalCTA />
    </SiteLayout>
  );
}
